import Ajv from 'ajv';

import { Graph } from '../../../data/misc';
import { getCollapsedGraph, getSubgraphIds } from '../graph/subgraph.utils';
import { GraphDatasetTableSettings } from './GraphDatasetProvider.types';

export const getGraphDebugTableData = (
  graphData: Graph,
  selectedAssistantName: string | undefined
): GraphDatasetTableSettings => {
  const subgraphSelection = getSubgraphIds(graphData.subgraphs);
  const collapsedGraph = getCollapsedGraph(graphData, subgraphSelection);

  const data: Record<string, any>[] = [];
  const presentSubgraphs = new Set<string>();

  for (const node of collapsedGraph.nodes) {
    const segments = node.id.split(':');
    const parentIds = segments.slice(0, -1);
    const id = segments.at(-1)!;

    const subgraphId = parentIds.join(':');
    const isInSubgraph =
      collapsedGraph.subgraphs.includes(subgraphId) &&
      subgraphSelection.includes(subgraphId);

    let label = node.id;
    if (isInSubgraph) {
      presentSubgraphs.add(subgraphId);
      label = id;
    }

    if (label === '__start__' || label === '__end__') continue;
    data.push({
      id: node.id,
      label,
      isInSubgraph,
      isSubgraph: collapsedGraph.subgraphs.includes(node.id),
    });
  }

  const finalData: GraphDatasetTableSettings = {
    graphs: [{ name: 'main', graphIndex: 0, isSubgraph: false, nodes: [] }],
  };

  data.forEach((item) => {
    if (!item.isInSubgraph) {
      finalData.graphs = finalData.graphs.map((graph) => {
        if (graph.isSubgraph) {
          return graph;
        }

        return {
          ...graph,
          nodes: [
            ...graph.nodes,
            {
              nodeId: item.id,
              nodeLabel: item.label,
              subgraphIndex: undefined,
              enabled: true,
              datasetNameOrId: constructDatasetString(
                selectedAssistantName,
                graph.name,
                item.id
              ),
            },
          ],
        };
      });
    } else {
      const subgraphNodeName = item.id.split(':').slice(-2, -1)[0];
      // Gets the third last element of the id, which is the entrypoint of the subgraph, fallback to 'main'.
      const graphEntrypoint = item.id.split(':').slice(-3, -2)[0] || 'main';

      // Check if subgraph already exists in finalData
      if (!finalData.graphs.find((graph) => graph.name === subgraphNodeName)) {
        const graphIndex = finalData.graphs.length;

        // Create new subgraph and add initial node
        finalData.graphs.push({
          name: subgraphNodeName,
          graphIndex: graphIndex,
          isSubgraph: true,
          nodes: [
            {
              nodeId: item.id,
              nodeLabel: item.label,
              subgraphIndex: undefined,
              enabled: true,
              datasetNameOrId: constructDatasetString(
                selectedAssistantName,
                subgraphNodeName,
                item.id
              ),
            },
          ],
        });

        const parentGraph = finalData.graphs.find(
          (g) => g.name === graphEntrypoint
        );
        if (!parentGraph) {
          // TODO: Better error handling here
          return;
        }

        // Add reference to this subgraph in parent graph if it doesn't exist
        const referenceNode = parentGraph.nodes.find(
          (n) => n.nodeId === subgraphNodeName
        );
        if (referenceNode) return;

        finalData.graphs = finalData.graphs.map((graph) => {
          if (graph.name === graphEntrypoint) {
            return {
              ...graph,
              nodes: [
                ...graph.nodes,
                {
                  nodeId: subgraphNodeName,
                  nodeLabel: subgraphNodeName,
                  subgraphIndex: graphIndex,
                  enabled: true,
                  datasetNameOrId: constructDatasetString(
                    selectedAssistantName,
                    graph.name,
                    subgraphNodeName
                  ),
                },
              ],
            };
          }

          return graph;
        });

        return;
      } else {
        // Add node to existing subgraph
        finalData.graphs = finalData.graphs.map((graph) => {
          if (graph.name === subgraphNodeName) {
            return {
              ...graph,
              nodes: [
                ...graph.nodes,
                {
                  nodeId: item.id,
                  nodeLabel: item.label,
                  subgraphIndex: undefined,
                  enabled: true,
                  datasetNameOrId: constructDatasetString(
                    selectedAssistantName,
                    graph.name,
                    item.id
                  ),
                },
              ],
            };
          }

          return graph;
        });
      }
    }
  });

  return finalData;
};

export const constructDatasetString = (
  selectedAssistantName: string | undefined,
  graphName: string,
  nodeId: string
): string => {
  const nodeIdStr = nodeId.includes(':')
    ? nodeId.split(':').slice(-1)[0]
    : nodeId;
  return `${
    selectedAssistantName ? `ASSISTANT:${selectedAssistantName}-` : ''
  }GRAPH:${graphName}-NODE:${nodeIdStr}`;
};

export const getDatasetNameOrIdFromNodeId = (
  graphData: GraphDatasetTableSettings,
  nodeId: string
): string | undefined => {
  const graph = graphData?.graphs?.find((graph) =>
    graph.nodes.find((node) => node.nodeId === nodeId)
  );
  if (!graph) return undefined;
  const node = graph.nodes.find((node) => node.nodeId === nodeId);
  return node?.datasetNameOrId;
};

const ajv = new Ajv({ allErrors: true, verbose: true });

export const getValidationErrors = (
  schema: Record<string, unknown> | undefined | null,
  data: unknown
) => {
  try {
    if (!schema) return null;
    const validator = ajv.compile(schema);
    const status = validator(data);
    if (!status) return ajv.errorsText(validator.errors);
    return null;
  } catch (e) {
    console.error(e);
    return null;
  }
};
